import { SearchIcon } from 'lucide-react';
import React, { useCallback, useEffect } from 'react';
import UIStrings from '@/Utils/UIStrings';
import { DialogEnum, useActionDialogContext } from '../../../components/dialogs/Context/ActionDialogContext';
import { mapFromCandidateOverviewToCandidateDialogModel } from '@/Pages/Dialogs/Dialogs.Utils';
import { useCandidatesOverviewContext } from '../../Contexts/CandidatesOverviewContext/CandidatesOverviewContext';
import AdvancedFiltersToolbar from '../List/AdvancedFiltersToolbar';
import CandidatesListToolTip from '../List/CandidatesListToolTip';
import ToolbarFilterButton from '../List/ToolbarFilterButton';
import { useCandidatesTableContext } from '../../Contexts/CandidatesTableContext/CandidatesTableContext';
import EditColumnsButton from './EditColumnsButton';
import TableToolbar from '@/Components/Table/TableToolbar';
import { Button, Spinner } from '@/Components/shadcn';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/Components/shadcn/input-group';

const TableFilters = () => {
    const { showDialog } = useActionDialogContext();
    const {
        assessmentId,
        selected,
        setSelected,
        testTypes,
        tabValue,
        searchByName,
        openFilter,
        search,
        includesExperienceCriteria,
        groupReportProcessing,
        getApplicantGroupReportResults,
    } = useCandidatesOverviewContext();
    const table = useCandidatesTableContext();

    const selectedMrtRows = table.getSelectedRowModel().rows;

    useEffect(() => {
        setSelected(selectedMrtRows.map((row) => row.original));
    }, [selectedMrtRows]);

    useEffect(() => {
        table.resetRowSelection();
    }, [tabValue]);

    const rejectDialogHandle = useCallback(() => {
        showDialog(
            selected.map((candidate) => mapFromCandidateOverviewToCandidateDialogModel(assessmentId, candidate)),
            DialogEnum.Reject,
            testTypes,
            includesExperienceCriteria,
        );
        table.resetRowSelection();
    }, [selected]);

    const handleGroupReport = useCallback(async () => {
        await getApplicantGroupReportResults();
        table.resetRowSelection();
    }, [selected]);

    return (
        <TableToolbar className="justify-between">
            <div className="min-w-0 flex-1 shrink-0">
                {selected.length > 0 && (
                    <div className="flex flex-row flex-wrap items-center gap-1.5">
                        <CandidatesListToolTip title={UIStrings.RejectApplicants}>
                            <Button
                                variant="destructive"
                                size="xs"
                                onClick={rejectDialogHandle}
                                data-cy="ToolbarGroupRemove"
                            >
                                {UIStrings.RejectByCount(selected.length)}
                            </Button>
                        </CandidatesListToolTip>
                        <Button
                            variant="outline"
                            size="xs"
                            disabled={groupReportProcessing}
                            onClick={handleGroupReport}
                            data-cy="ToolbarCreateApplicantsPresentation"
                        >
                            {groupReportProcessing ? (
                                <>
                                    <Spinner data-icon="inline-start" />
                                    {UIStrings.CreateApplicantsPresentation}
                                </>
                            ) : (
                                UIStrings.CreateApplicantsPresentation
                            )}
                        </Button>
                    </div>
                )}
            </div>
            <div className="flex shrink-0 flex-row flex-wrap items-center justify-end gap-2.5">
                {openFilter && <AdvancedFiltersToolbar />}
                <InputGroup className="w-64" data-slot="toolbar-search-root">
                    <InputGroupAddon>
                        <SearchIcon />
                    </InputGroupAddon>
                    <InputGroupInput
                        value={search}
                        data-cy="ToolbarSearch"
                        placeholder={UIStrings.SearchByName}
                        aria-label={UIStrings.SearchByName}
                        onChange={(e) => searchByName(e.target.value)}
                    />
                </InputGroup>
                {tabValue === 'Open' && <ToolbarFilterButton />}
                <EditColumnsButton />
            </div>
        </TableToolbar>
    );
};

export default TableFilters;
